import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  ScrollText,
  Loader2,
  AlertCircle,
  FileText,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  GENERATE_STATEMENT,
  GET_INVOICES_BY_ORGANIZATION,
  GET_STATEMENTS_BY_ORGANIZATION,
  type GetInvoicesByOrganizationResponse,
} from '@/graphql/invoices';

// ==================== Helpers ====================

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function firstOfMonth(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
}

function lastOfMonth(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 0);
  return d.toISOString().split('T')[0];
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

// ==================== Component Props ====================

interface GenerateStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

// ==================== Component ====================

export function GenerateStatementModal({
  isOpen,
  onClose,
  clientId,
}: GenerateStatementModalProps) {
  // Form state
  const [billingPeriodStart, setBillingPeriodStart] = useState(firstOfMonth());
  const [billingPeriodEnd, setBillingPeriodEnd] = useState(lastOfMonth());
  const [statementDate, setStatementDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(addDays(lastOfMonth(), 30));
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  // Fetch invoices to preview eligible ones
  const { data: invoicesData, loading: invoicesLoading } = useQuery<GetInvoicesByOrganizationResponse>(
    GET_INVOICES_BY_ORGANIZATION,
    {
      variables: { organizationId: clientId },
      skip: !isOpen || !clientId,
    }
  );

  // Mutation
  const [generateStatement, { loading: generating }] = useMutation(GENERATE_STATEMENT, {
    refetchQueries: [
      { query: GET_STATEMENTS_BY_ORGANIZATION, variables: { organizationId: clientId } },
      { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  });

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      setBillingPeriodStart(firstOfMonth());
      setBillingPeriodEnd(lastOfMonth());
      setStatementDate(todayISO());
      setDueDate(addDays(lastOfMonth(), 30));
      setNotes('');
      setErrors([]);
    }
  }, [isOpen]);

  // Auto-update due date when billing period end changes
  useEffect(() => {
    if (billingPeriodEnd) {
      setDueDate(addDays(billingPeriodEnd, 30));
    }
  }, [billingPeriodEnd]);

  // Preview: eligible invoices (approved, unbilled, in date range)
  const eligibleInvoices = useMemo(() => {
    if (!invoicesData?.invoicesByOrganization) return [];

    return invoicesData.invoicesByOrganization.filter((inv) => {
      // Must be approved
      if (inv.status !== 'APPROVED') return false;
      // Must not already be on a statement
      if (inv.statementId) return false;
      // Must fall within billing period
      const invDate = new Date(inv.invoiceDate);
      const start = new Date(billingPeriodStart);
      const end = new Date(billingPeriodEnd);
      return invDate >= start && invDate <= end;
    });
  }, [invoicesData, billingPeriodStart, billingPeriodEnd]);

  const eligibleTotal = eligibleInvoices.reduce((sum, inv) => sum + inv.total, 0);

  // ==================== Validation ====================

  function validate(): string[] {
    const errs: string[] = [];
    if (!billingPeriodStart) errs.push('Billing period start is required.');
    if (!billingPeriodEnd) errs.push('Billing period end is required.');
    if (billingPeriodStart && billingPeriodEnd && billingPeriodStart > billingPeriodEnd) {
      errs.push('Billing period start must be before end.');
    }
    if (!statementDate) errs.push('Statement date is required.');
    if (!dueDate) errs.push('Due date is required.');
    if (eligibleInvoices.length === 0) {
      errs.push('No approved, unbilled invoices found in this billing period.');
    }
    return errs;
  }

  // ==================== Submit ====================

  async function handleSubmit() {
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);

    try {
      const input = {
        organizationId: clientId,
        billingPeriodStart,
        billingPeriodEnd,
        statementDate,
        dueDate,
        notes: notes.trim() || undefined,
      };

      await generateStatement({ variables: { input } });
      onClose();
    } catch (err: unknown) {
      setErrors([err instanceof Error ? err.message : 'Failed to generate statement.']);
    }
  }

  // ==================== Render ====================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generate Statement"
      size="lg"
      footer={
        <>
          <button onClick={onClose} disabled={generating} className="btn-ghost px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={generating || eligibleInvoices.length === 0}
            className="btn-primary px-4 py-2 text-sm"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />
            ) : (
              <ScrollText className="w-4 h-4 mr-1.5 inline" />
            )}
            Generate Statement
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rust dark:text-salmon mt-0.5 flex-shrink-0" />
              <ul className="text-sm text-rust dark:text-salmon space-y-0.5">
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Billing Period */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Billing Period <span className="text-rust">*</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-chalkboard dark:text-rain-cloud mb-1">Start</label>
              <input
                type="date"
                value={billingPeriodStart}
                onChange={(e) => setBillingPeriodStart(e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-chalkboard dark:text-rain-cloud mb-1">End</label>
              <input
                type="date"
                value={billingPeriodEnd}
                onChange={(e) => setBillingPeriodEnd(e.target.value)}
                className="input w-full"
              />
            </div>
          </div>
        </div>

        {/* Statement Date + Due Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Statement Date <span className="text-rust">*</span>
            </label>
            <input
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Due Date <span className="text-rust">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-chalkboard/60 dark:text-rain-cloud/60 mt-1">
              Net 30 from billing period end
            </p>
          </div>
        </div>

        {/* Preview: Eligible Invoices */}
        <div>
          <h3 className="text-sm font-medium text-slate dark:text-mist mb-2">
            Eligible Invoices Preview
          </h3>

          {invoicesLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-pond dark:text-pond-light" />
              <span className="ml-2 text-sm text-chalkboard dark:text-rain-cloud">
                Loading invoices...
              </span>
            </div>
          ) : eligibleInvoices.length === 0 ? (
            <div className="border border-dashed border-gray dark:border-night-pond rounded-lg p-4 text-center">
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                No approved, unbilled invoices found in the selected billing period.
              </p>
            </div>
          ) : (
            <>
              {/* Summary Banner */}
              <div className="bg-turquoise/5 dark:bg-aqua/10 border border-turquoise/20 dark:border-aqua/20 rounded-lg p-3 mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-turquoise dark:text-aqua" />
                  <span className="text-sm font-medium text-slate dark:text-mist">
                    {eligibleInvoices.length} invoice{eligibleInvoices.length !== 1 ? 's' : ''} will be grouped
                  </span>
                </div>
                <span className="text-sm font-semibold text-slate dark:text-mist">
                  {formatCurrency(eligibleTotal)}
                </span>
              </div>

              {/* Invoice List */}
              <div className="card overflow-hidden">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th className="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleInvoices.map((inv) => (
                      <tr key={inv.id}>
                        <td className="font-medium text-slate dark:text-mist text-sm">
                          {inv.invoiceNumber}
                        </td>
                        <td className="text-chalkboard dark:text-rain-cloud text-sm">
                          {formatDate(inv.invoiceDate)}
                        </td>
                        <td className="text-right font-medium text-slate dark:text-mist text-sm">
                          {formatCurrency(inv.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="textarea w-full"
            placeholder="Optional statement notes..."
          />
        </div>
      </div>
    </Modal>
  );
}
